import type { LinhaDeAtividade } from '../../api/visao-geral.js';
import { formatarHora } from '../../componentes/datas.js';
import { rotuloDoLocal } from '../../componentes/deposito.js';
import { EstadoVazio } from '../../componentes/EstadoVazio.js';
import { IconeAuditoria } from '../../componentes/Icones.js';
import './AtividadeRecente.css';

/**
 * "O que acabou de acontecer" — as últimas linhas da trilha de auditoria.
 *
 * O servidor manda o CÓDIGO da ação e um recorte por lista branca do contexto;
 * a frase é montada aqui, com a mesma disciplina de `api/erros.ts` — a tela
 * decide o texto pelo código, nunca pela prosa do middleware.
 *
 * O horário é o do SERVIDOR (quando a trilha recebeu), e não o do aparelho:
 * a pergunta desta lista é "o que acabou de chegar", e um coletor que passou a
 * manhã em modo avião entregaria a fila inteira com hora da manhã, embaralhada
 * com o que está acontecendo agora.
 */

function comQuantidade(verbo: string, item: string | null, quantidade: string | null): string {
  if (item === null) {
    return verbo;
  }

  return quantidade === null ? `${verbo} ${item}` : `${verbo} ${quantidade} un de ${item}`;
}

/**
 * A frase da linha, a partir do código da ação.
 *
 * Ação fora do catálogo aparece pelo próprio código: inventar uma redação
 * plausível para um evento que ninguém previu é como o painel passa a afirmar
 * o que não sabe.
 */
export function textoDaAtividade(linha: LinhaDeAtividade): string {
  const { contexto } = linha;

  switch (linha.acao) {
    case 'coleta.registrada':
      return comQuantidade('Coletou', contexto.sku, contexto.quantidade);
    case 'movimentacao.registrada':
      return comQuantidade('Movimentou', contexto.produto, contexto.quantidade);
    case 'contagem_inventario.registrada':
      return 'Registrou contagem de inventário';
    case 'inventario.conflito_detectado':
      return 'Conflito entre contagens do mesmo local';
    case 'substituicao_sku.registrada':
      return contexto.sku === null
        ? 'Registrou substituição de SKU'
        : `Substituiu o SKU ${contexto.sku}`;
    default:
      return linha.acao;
  }
}

/** Local da linha — filial · depósito da ação, ou o de destino, ou o vazio explícito. */
function localDaAtividade(linha: LinhaDeAtividade): string {
  const { codFil, depositoErp, depositoDestino } = linha.contexto;
  return rotuloDoLocal(codFil, depositoErp ?? depositoDestino);
}

export function AtividadeRecente({ linhas }: { readonly linhas: readonly LinhaDeAtividade[] }) {
  if (linhas.length === 0) {
    return (
      <EstadoVazio
        tom="neutro"
        icone={<IconeAuditoria tamanho={28} />}
        titulo="Nenhuma atividade registrada nesta janela."
        detalhe="A trilha recebe uma linha a cada coleta, movimentação ou contagem que chega do coletor."
      />
    );
  }

  return (
    <ul className="atividade">
      {linhas.map((linha) => (
        <li key={linha.id} className="atividade__linha">
          {/* Matrícula do operador ou login do painel — a trilha guarda o autor, não o nome. */}
          <span className="atividade__operador">{linha.autor ?? '—'}</span>
          <span className="atividade__acao">{textoDaAtividade(linha)}</span>
          <span className="atividade__local mono">{localDaAtividade(linha)}</span>
          <span className="atividade__hora mono">{formatarHora(linha.registradoEm)}</span>
        </li>
      ))}
    </ul>
  );
}
